import React from 'react'
import {Icon} from 'src/common/Icon'
import {sanitizeHtml} from 'src/common/helpers/sanitizeHtml'
import {fmtFull, fmtShort, pos, TICKS} from '../data/helpers'
import type {CategoryData} from '../data/types'

interface DistributionBoxPlotsProps {
  data: CategoryData
}

const DistributionBoxPlots: React.FC<DistributionBoxPlotsProps> = ({data}) => {
  const {distribution} = data

  return (
    <section className="section white">
      <div className="wrap">
        <div className="subsection">
          <div className="sub-eyebrow">The full distribution</div>
          <h3>What the averages hide.</h3>
          <p className="sub-intro" dangerouslySetInnerHTML={{__html: sanitizeHtml(distribution.intro)}} />
          <div className="spread-howto">
            <div className="howto-header">
              <div className="howto-label">How to read these charts</div>
              <Icon name="moss-logo-v2" className="chart-logo" />
            </div>
            <svg
              className="howto-svg"
              viewBox="0 0 1000 300"
              role="img"
              aria-label="How to read the box plots and their logarithmic spend axis"
            >
              <defs>
                <marker
                  id="howtoArrow"
                  markerWidth="9"
                  markerHeight="9"
                  refX="4.5"
                  refY="4.5"
                  orient="auto-start-reverse"
                >
                  <path d="M0 0 L9 4.5 L0 9 Z" fill="#131212" />
                </marker>
              </defs>
              <rect x="250" y="104" width="370" height="16" rx="8" fill="rgba(19,18,18,0.13)" />
              <rect x="247" y="98" width="3" height="28" fill="rgba(19,18,18,0.35)" />
              <rect x="618" y="98" width="3" height="28" fill="rgba(19,18,18,0.35)" />
              <line
                x1="621"
                y1="112"
                x2="889"
                y2="112"
                stroke="rgba(19,18,18,0.3)"
                strokeWidth="2"
                strokeDasharray="2 5"
                strokeLinecap="round"
              />
              <rect x="372" y="97" width="6" height="30" rx="2" fill="#131212" />
              <circle cx="900" cy="112" r="12" fill="#B4FF44" stroke="none" />
              <circle cx="900" cy="112" r="12" fill="none" stroke="#B4FF44" strokeOpacity="0.3" strokeWidth="7" />
              <text
                x="375"
                y="34"
                textAnchor="middle"
                fontFamily="Systemia, sans-serif"
                fontSize="17"
                fontWeight="600"
                fill="#131212"
              >
                Typical company
              </text>
              <text x="375" y="54" textAnchor="middle" fontFamily="Systemia, sans-serif" fontSize="13" fill="#4A4A4A">
                half pay less than this, half pay more
              </text>
              <line x1="375" y1="66" x2="375" y2="92" stroke="#131212" strokeWidth="1.5" markerEnd="url(#howtoArrow)" />
              <text
                x="900"
                y="34"
                textAnchor="middle"
                fontFamily="Systemia, sans-serif"
                fontSize="17"
                fontWeight="600"
                fill="#131212"
              >
                Top of the range
              </text>
              <text x="900" y="54" textAnchor="middle" fontFamily="Systemia, sans-serif" fontSize="13" fill="#4A4A4A">
                the most anyone pays
              </text>
              <line x1="900" y1="66" x2="900" y2="96" stroke="#131212" strokeWidth="1.5" markerEnd="url(#howtoArrow)" />
              <path d="M250 140 V150 H620 V140" fill="none" stroke="#131212" strokeWidth="1.5" />
              <line
                x1="435"
                y1="150"
                x2="435"
                y2="172"
                stroke="#131212"
                strokeWidth="1.5"
                markerEnd="url(#howtoArrow)"
              />
              <text
                x="435"
                y="192"
                textAnchor="middle"
                fontFamily="Systemia, sans-serif"
                fontSize="17"
                fontWeight="600"
                fill="#131212"
              >
                80% of companies fall in this range
              </text>
              <text x="435" y="210" textAnchor="middle" fontFamily="Systemia, sans-serif" fontSize="13" fill="#4A4A4A">
                from the lightest 10% to the heaviest 10%
              </text>
              <line x1="250" y1="252" x2="900" y2="252" stroke="rgba(19,18,18,0.25)" strokeWidth="1.5" />
              <g fontFamily="Systemia, sans-serif" fontSize="12" fill="#4A4A4A" textAnchor="middle">
                <line x1="250" y1="248" x2="250" y2="256" stroke="rgba(19,18,18,0.3)" strokeWidth="1.5" />
                <text x="250" y="272">
                  €10
                </text>
                <line x1="412" y1="248" x2="412" y2="256" stroke="rgba(19,18,18,0.3)" strokeWidth="1.5" />
                <text x="412" y="272">
                  €100
                </text>
                <line x1="575" y1="248" x2="575" y2="256" stroke="rgba(19,18,18,0.3)" strokeWidth="1.5" />
                <text x="575" y="272">
                  €1K
                </text>
                <line x1="737" y1="248" x2="737" y2="256" stroke="rgba(19,18,18,0.3)" strokeWidth="1.5" />
                <text x="737" y="272">
                  €10K
                </text>
                <line x1="900" y1="248" x2="900" y2="256" stroke="rgba(19,18,18,0.3)" strokeWidth="1.5" />
                <text x="900" y="272">
                  €100K
                </text>
              </g>
              <text x="575" y="294" textAnchor="middle" fontFamily="Systemia, sans-serif" fontSize="13" fill="#4A4A4A">
                The spend axis is{' '}
                <tspan fontWeight="600" fill="#131212">
                  logarithmic
                </tspan>
                , each step is 10× more, so €10 and €100K both fit on one line.
              </text>
            </svg>
          </div>
          <div className="spread-grid">
            {distribution.tools.map((tool) => (
              <div key={tool.name} className="spread-card">
                <div className="spread-header">
                  <div className="spread-name">
                    <span className="dot" style={{background: tool.color}} />
                    {tool.name}
                  </div>
                </div>
                {tool.rows.map((r) => (
                  <div key={r.size} className="spread-row">
                    <div className="size-label">
                      <strong>{r.size}</strong>
                    </div>
                    <div className="spread-track">
                      <div
                        className="band"
                        style={{
                          left: `${pos(r.p10)}%`,
                          width: `${Math.max(2, pos(r.p90) - pos(r.p10))}%`,
                        }}
                      />
                      <div
                        className="max-connector"
                        style={{
                          left: `${pos(r.p90)}%`,
                          width: `${Math.max(0, pos(r.max) - pos(r.p90))}%`,
                        }}
                      />
                      <div className="median" style={{left: `${pos(r.median)}%`}}>
                        <div className="median-label">{fmtFull(r.median)}</div>
                      </div>
                      <div className="max-marker" style={{left: `${pos(r.max)}%`}} />
                      <div className="max-label" style={{left: `${pos(r.max)}%`}}>
                        {fmtShort(r.max)}
                      </div>
                    </div>
                    <div
                      className="spread-takeaway-inline"
                      dangerouslySetInnerHTML={{__html: sanitizeHtml(r.takeaway)}}
                    />
                  </div>
                ))}
                <div className="spread-axis spread-axis-bottom">
                  {TICKS.map((v) => (
                    <div key={v} className="tick" style={{left: `${pos(v)}%`}}>
                      {fmtShort(v)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {distribution.insight && (
            <div className="cat-insight" dangerouslySetInnerHTML={{__html: sanitizeHtml(distribution.insight)}} />
          )}
        </div>
      </div>
    </section>
  )
}

export default DistributionBoxPlots
